import { format } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import { IoCloseCircleOutline } from "react-icons/io5";
import orgApi from "../../../api/org_api";
import PaginationButtons from "../../../component/PaginationButtons";
import {
  Breadcrumb,
  Button,
  CustomDatePicker,
  FooterButtons,
  FunnelIconButton,
  IconButton,
  Paragraph,
  Table,
  Text,
} from "../../../component/common";
import { NextSevenDays } from "../../../utils/constant";

interface ScheduleItem {
  id: number;
  post: {
    job_title: string;
    client_name: string;
  };
  org: {
    org_name: string;
    first_name: string;
    last_name: string;
  };
  interviewer: {
    candidate: {
      first_name: string;
      last_name: string;
    };
  } | null;
  utc_starttime: string;
  meeting_id: string;
  candidate: {
    first_name: string;
    last_name: string;
    email: string;
    resume: {
      url: string;
      type: string;
    } | null;
  };
  status: string;
}

interface CsvDataItem {
  JobTittle: string;
  OrgName: string;
  RecruiterName: string;
  ClientName: string;
  InterviewerName: string;
  ScheduleStartTime: string;
  MeetingId: string;
  CandidateName: string;
  CandidateEmail: string;
  Status: string;
}

const Report = () => {
  const [schedule, setSchedule] = useState<ScheduleItem[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [dateFilter, setDateFilter] = useState<{
    from: Date | null;
    to: Date | null;
  }>({ from: new Date(), to: NextSevenDays });
  const [totalPost, setTotal] = useState<number>(0);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [csvData, setCsvData] = useState<CsvDataItem[]>([]);

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  const fetchData = async () => {
    try {
      const page = totalPages === 0 ? 1 : Math.min(currentPage + 1, totalPages);
      const org = JSON.parse(
        localStorage.getItem("user__details")?.toString() || "{}",
      );
      const reqData = {
        page,
        ...getBodyDate(dateFilter.from, dateFilter.to),
      };
      const res = await orgApi.scheduleReport(org.id, reqData);

      if (res.data.success) {
        setTotal(res.data.total);
        const totalPagesCount = Math.ceil(res.data.total / 10);
        setTotalPages(totalPagesCount);
        setSchedule(res.data.schedule || []);
      } else {
        setSchedule([]);
        setTotal(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setSchedule([]);
      setTotal(0);
      setTotalPages(0);
    }
  };

  const getBodyDate = (
    start: Date | null,
    end: Date | null,
  ): { from: string; to: string } => {
    const from = start
      ? format(start, "yyyy-MM-dd 00:00:00")
      : !start && end
        ? format(new Date(), "yyyy-MM-dd 00:00:00")
        : "";

    const to = end
      ? format(end, "yyyy-MM-dd 23:59:59")
      : !end && start
        ? format(new Date(), "yyyy-MM-dd 23:59:59")
        : "";

    return { from, to };
  };

  const clearFilter = () => {
    setDateFilter({ from: new Date(), to: NextSevenDays });
  };

  const exportData = async () => {
    try {
      const org = JSON.parse(
        localStorage.getItem("user__details")?.toString() || "{}",
      );
      const reqData = getBodyDate(dateFilter.from, dateFilter.to);
      const res = await orgApi.exportScheduleReport(org.id, reqData);

      if (res.data.success) {
        const datas = res.data.schedule || [];
        const data: CsvDataItem[] = datas.map((element: ScheduleItem) => ({
          JobTittle: element.post.job_title,
          OrgName: element.org.org_name,
          RecruiterName: `${element.org.first_name} ${element.org.last_name}`,
          ClientName: element.post.client_name,
          InterviewerName: element.interviewer
            ? `${element.interviewer.candidate.first_name} ${element.interviewer.candidate.last_name}`
            : "",
          ScheduleStartTime: format(
            new Date(element.utc_starttime),
            "dd MMM yyyy hh:mm a",
          ),
          MeetingId: element.meeting_id,
          CandidateName: `${element.candidate.first_name} ${element.candidate.last_name}`,
          CandidateEmail: element.candidate.email,
          Status: element.status,
        }));

        setCsvData(data);
        setTimeout(() => {
          document.getElementById("expbtn")?.click();
        }, 1000);
      }
    } catch (error) {
      console.error("Error exporting data:", error);
    }
  };

  const REPORT_BREADCRUMBS = [
    { text: "Reports", href: "/report_list" },
    { text: `Schedule Report ${`(${totalPost || 0})`}`, isActive: true },
  ];

  const getScheduleReport = () => {
    const columns = [
      // { label: "S.No", accessor: "s_no" },
      { label: "Post Title", accessor: "post_title" },
      { label: "Recruiter Name", accessor: "recruiter_name" },
      { label: "Client Name", accessor: "client_name" },
      { label: "Status", accessor: "status" },
      { label: "Interviewer Name", accessor: "interviewer_name" },
      { label: "Interview Date", accessor: "interview_date" },
      { label: "Interview Time", accessor: "interview_time" },
      { label: "Meeting ID", accessor: "meeting_id" },
      { label: "Candidate Name", accessor: "candidate_name" },
      { label: "Candidate Email", accessor: "candidate_email" },
      { label: "Resume", accessor: "resume" },
    ];

    const data = schedule.map((value: ScheduleItem, index: number) => {
      const resumeData = value?.candidate?.resume;
      let resumeUrl: string | null = null;
      if (resumeData && Array.isArray(resumeData)) {
        resumeUrl = resumeData[0]?.url || null;
      }
      const resumeDisplay = resumeUrl ? (
        <a
          href={resumeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          View
        </a>
      ) : (
        <Text text="Not available" />
      );

      return {
        s_no: <Text text={String(index + 1 + currentPage * 10)} />,
        post_title: <Text text={value?.post?.job_title || "-"} />,
        recruiter_name: (
          <Text
            text={
              `${value?.org?.first_name || ""} ${
                value?.org?.last_name || ""
              }`.trim() || "-"
            }
          />
        ),
        client_name: <Text text={value?.post?.client_name || "-"} />,
        status: <Text text={value?.status || "-"} />,
        interviewer_name: (
          <Text
            text={
              value?.interviewer
                ? `${value.interviewer.candidate.first_name} ${value.interviewer.candidate.last_name}`
                : "-"
            }
          />
        ),
        interview_date: (
          <Text text={format(new Date(value?.utc_starttime), "dd MMM yyyy")} />
        ),
        interview_time: (
          <Text text={format(new Date(value?.utc_starttime), "hh:mm a")} />
        ),
        meeting_id: <Text text={value?.meeting_id || "-"} />,
        candidate_name: (
          <Text
            text={
              `${value?.candidate?.first_name || ""} ${
                value?.candidate?.last_name || ""
              }`.trim() || "-"
            }
          />
        ),
        candidate_email: <Text text={value?.candidate?.email || "-"} />,
        resume: resumeDisplay,
      };
    });

    return { columns, data };
  };

  return (
    <div className="space-y-4 w-[calc(100vw-120px)] 2xl:w-[calc(100vw-155px)]">
      <div className="flex justify-between items-start">
        <Breadcrumb items={REPORT_BREADCRUMBS} />
        <div className="flex justify-between items-start">
          <div className="flex flex-row items-center gap-2">
            <div className="flex flex-row items-center gap-2 justify-end">
              <CSVLink
                className="bg-primary rounded px-2.5 py-1.5 text-xs text-white hidden"
                type="button"
                filename="ScheduleReport.csv"
                data={csvData}
                id="expbtn"
              >
                Export to CSV
              </CSVLink>
              <Button
                type="button"
                text="Export to CSV"
                onClick={exportData}
                className="py-1.5 text-[12px]"
              />
            </div>
            <FunnelIconButton
              onClick={() => setShowFilter(!showFilter)}
              active={!!dateFilter.from || !!dateFilter.to}
            />
            <AnimatePresence>
              {showFilter && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-6 top-[110px] z-20 w-60 rounded-lg border border-gray-200 bg-white p-3 shadow-lg 2xl:w-72 2xl:p-4"
                >
                  <div className="space-y-2 2xl:space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <Paragraph text="Filter" color="text-gray-500" />
                      <IconButton
                        icon={
                          <IoCloseCircleOutline className="h-3.5 w-3.5 2xl:h-4 2xl:w-4" />
                        }
                        onClick={() => {
                          clearFilter();
                          setShowFilter(false);
                        }}
                        color="red"
                        className="-my-1 -mr-1 p-1"
                      />
                    </div>
                    <div className="space-y-2">
                      <CustomDatePicker
                        label="Start Date"
                        selected={dateFilter.from}
                        onChange={(date) =>
                          setDateFilter({ ...dateFilter, from: date })
                        }
                        showYearDropdown
                        showMonthDropdown
                        maxDate={dateFilter.to || new Date()}
                      />
                      <CustomDatePicker
                        label="End Date"
                        selected={dateFilter.to}
                        onChange={(date) =>
                          setDateFilter({ ...dateFilter, to: date })
                        }
                        minDate={dateFilter.from || undefined}
                        showYearDropdown
                        showMonthDropdown
                        // maxDate={new Date()}
                      />
                    </div>
                    <FooterButtons
                      primaryOnClick={() => {
                        setDateFilter({
                          from: dateFilter.from,
                          to: dateFilter.to,
                        });
                        fetchData();
                        setShowFilter(false);
                      }}
                      secondaryOnClick={() => {
                        setDateFilter({ from: new Date(), to: NextSevenDays });
                      }}
                      primaryText="Apply"
                      secondaryText="Reset"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      <Table
        columns={getScheduleReport().columns}
        data={getScheduleReport().data}
        isStickyHeader
        containerHeight="h-[calc(100vh-197px)] 2xl:h-[calc(100vh-205px)] "
        isStacicRow
      />
      {totalPost > 10 && (
        <PaginationButtons
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}
    </div>
  );
};

export default Report;
